/**
 * Sidebar Component - Core Carousel Navigation
 *
 * Fixed left sidebar following the Core Carousel Figma design:
 * - Logo mark + "CORE CAROUSEL" at top
 * - Navigation items with icons and expandable submenus
 * - Active state with purple pill highlight
 * - User profile section at bottom
 * - Collapsible on smaller screens
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import CoreStudioLogo from '@/components/ui/core-studio-logo';
import CoreStudioMark from '@/components/ui/core-studio-mark';
import { cn } from '@/lib/utils';
import { getProfile } from '@/services/profileService';
import { Language, Profile } from '@/types';
import { getDisplayName, PROFILE_UPDATED_EVENT } from '@/lib/displayName';
import {
  LayoutDashboard,
  CreditCard,
  Settings,
  ChevronDown,
  ChevronRight,
  Globe,
  LogOut,
  Moon,
  Sun,
  PanelLeftClose,
  PanelLeft,
  X,
  RotateCcw,
  Shield,
  Sparkles,
  FolderOpen,
  MessageCircleQuestion,
} from 'lucide-react';
import { openHelpChat, resetHelpSession, useHelpEnabled } from '@/components/help/helpChatEvents';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { checkAdminRole } from '@/services/adminService';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path?: string;
  action?: () => void;
  children?: { id: string; label: string; path: string }[];
}

const BASE_NAV_ITEMS: NavItem[] = [
  {
    id: 'newChat',
    label: 'Novo chat',
    icon: Sparkles,
    path: '/',
  },
  {
    id: 'projects',
    label: 'Projetos',
    icon: FolderOpen,
    path: '/projects',
  },
  {
    id: 'plans',
    label: 'Meus Planos',
    icon: CreditCard,
    path: '/meus-planos',
  },
  {
    id: 'help',
    label: 'Ajuda',
    icon: MessageCircleQuestion,
    action: () => openHelpChat(),
  },
];

interface SidebarProps {
  editorTheme?: 'light' | 'dark';
  onThemeToggle?: () => void;
  onClose?: () => void;
  onHide?: () => void;
  forceExpanded?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ editorTheme = 'dark', onThemeToggle, onClose, onHide, forceExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useTranslation();
  const { user, signOut } = useAuth();
  const { restartTour } = useOnboarding();
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const helpEnabled = useHelpEnabled();

  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [languageExpanded, setLanguageExpanded] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drawer mode: force expanded, no width transitions
  const isDrawer = !!onClose;
  const effectiveCollapsed = forceExpanded ? false : collapsed;

  const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
    { value: 'pt-BR', label: t('language.pt-BR') },
    { value: 'en', label: t('language.en') },
    { value: 'es', label: t('language.es') },
  ];

  const toggleMenu = (id: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isActive = (item: NavItem): boolean => {
    if (!item.path && !item.children) return false;
    if (item.path && item.path !== '/') {
      return location.pathname === item.path;
    }
    if (item.children) {
      return item.children.some((child) => location.pathname === child.path && child.path !== '/');
    }
    return item.id === 'dashboard' && location.pathname === '/';
  };

  const handleNavClick = (path?: string) => {
    if (path) {
      navigate(path);
      onClose?.();
    }
  };

  const handleSignOut = async () => {
    onClose?.();
    await signOut();
    resetHelpSession(); // the next login in this tab must not reuse this user's chat
    navigate('/login');
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setUserProfile(null);
        return;
      }
      const profile = await getProfile(user.id);
      setUserProfile(profile);
    };

    const handleProfileUpdated = () => {
      loadProfile();
    };

    loadProfile();
    window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
    };
  }, [user?.id]);

  // Check admin role (cached, one call per mount)
  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      return;
    }
    checkAdminRole()
      .then((data) => setIsAdmin(data.isAdmin))
      .catch(() => setIsAdmin(false));
  }, [user?.id]);

  // Build nav items with conditional admin submenu; "Ajuda" only when the help chat is on for this user
  const navItems: NavItem[] = useMemo(() => [
    ...BASE_NAV_ITEMS.filter((item) => item.id !== 'help' || helpEnabled === true),
    ...(isAdmin ? [{
      id: 'admin',
      label: 'Admin',
      icon: Shield,
      children: [
        { id: 'admin-dashboard', label: 'Dashboard', path: '/admin' },
        { id: 'grow-ops', label: 'Grow Ops', path: '/admin/grow-ops' },
      ],
    }] : []),
  ], [isAdmin, helpEnabled]);

  // Auto-expand admin submenu once when admin role is confirmed and user is on admin route
  const adminExpandedRef = useRef(false);
  useEffect(() => {
    if (isAdmin && !adminExpandedRef.current && location.pathname.startsWith('/admin')) {
      adminExpandedRef.current = true;
      setExpandedMenus(prev => new Set([...prev, 'admin']));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (effectiveCollapsed) {
      setSettingsExpanded(false);
      setLanguageExpanded(false);
    }
  }, [effectiveCollapsed]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Listen for onboarding tour actions (e.g. open settings flyout before a step)
  useEffect(() => {
    const handler = (e: Event) => {
      const action = (e as CustomEvent).detail?.action;
      if (action === 'open-settings-flyout') {
        setSettingsExpanded(true);
      }
    };
    window.addEventListener('cai:tour-action', handler);
    return () => window.removeEventListener('cai:tour-action', handler);
  }, []);

  const cancelCloseMenus = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleCloseMenus = () => {
    cancelCloseMenus();
    closeTimeoutRef.current = setTimeout(() => {
      setSettingsExpanded(false);
      setLanguageExpanded(false);
    }, 140);
  };

  const displayName = getDisplayName(userProfile, user);
  const displayInitial = displayName.charAt(0).toUpperCase() || 'U';

  return (
    <aside
      className={cn(
        'flex flex-col h-screen bg-card border-r border-border shrink-0 overflow-y-auto',
        isDrawer ? 'mobile-safe-bottom' : 'transition-all duration-300',
        effectiveCollapsed ? 'w-[68px]' : 'w-[260px]'
      )}
    >
      {/* Logo Section */}
      <div className={cn(
        'flex items-center gap-3 px-6 h-16 border-b border-border shrink-0',
        effectiveCollapsed && 'justify-center px-3'
      )}>
        {effectiveCollapsed ? (
          <CoreStudioMark size={28} />
        ) : (
          <CoreStudioLogo width={130} className="text-foreground" />
        )}
        {!effectiveCollapsed && (
          <>
            {/* Hide sidebar button (desktop only) */}
            {onHide && (
              <button
                onClick={onHide}
                className="ml-auto p-1.5 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors hidden md:flex"
                title={t('sidebar.hide')}
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
            {/* Close button (mobile drawer only) */}
            {onClose && (
              <button
                onClick={onClose}
                className="ml-auto p-1.5 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors md:hidden"
                aria-label={t('mobile.closeMenu')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav data-tour="sidebar-nav" className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          const hasChildren = !!item.children;
          const isExpanded = expandedMenus.has(item.id);

          return (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (item.action) {
                    item.action();
                    onClose?.();
                    return;
                  }
                  if (hasChildren) {
                    toggleMenu(item.id);
                  } else {
                    handleNavClick(item.path);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  effectiveCollapsed && 'justify-center px-0'
                )}
                title={effectiveCollapsed ? t(`sidebar.${item.id}`) : undefined}
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                {!effectiveCollapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{t(`sidebar.${item.id}`)}</span>
                    {hasChildren && (
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform',
                          isExpanded && 'rotate-180'
                        )}
                      />
                    )}
                  </>
                )}
              </button>

              {/* Submenu */}
              {hasChildren && isExpanded && !effectiveCollapsed && (
                <div className="mt-1 ml-4 pl-4 border-l border-border/50 space-y-0.5">
                  {item.children!.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleNavClick(child.path)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                        location.pathname === child.path && child.path !== '/'
                          ? 'text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      )}
                    >
                      {t(`sidebar.${child.id}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}


      </nav>

      {/* Bottom Section */}
      <div className="p-3 space-y-2 shrink-0">
        {/* Settings (bottom, flyout opens upward) */}
        <div
          className="relative"
          onMouseEnter={() => {
            if (effectiveCollapsed) return;
            cancelCloseMenus();
            setSettingsExpanded(true);
          }}
          onMouseLeave={() => {
            if (effectiveCollapsed) return;
            scheduleCloseMenus();
          }}
        >
          <button
            className={cn(
              'group flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium transition-colors',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
              effectiveCollapsed && 'justify-center px-0'
            )}
            title={effectiveCollapsed ? t('sidebar.settings') : undefined}
            aria-expanded={!effectiveCollapsed ? settingsExpanded : false}
            aria-controls="sidebar-settings-flyout"
            data-tour="sidebar-settings"
          >
            <Settings className="h-5 w-5 shrink-0" />
            {!effectiveCollapsed && (
              <>
                <span className="text-left truncate">{t('sidebar.settings')}</span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 ml-auto shrink-0 transition-transform',
                    settingsExpanded && 'rotate-180'
                  )}
                />
              </>
            )}
          </button>

          {!effectiveCollapsed && settingsExpanded && (
            <div
              id="sidebar-settings-flyout"
              className="absolute left-0 right-0 bottom-full mb-2 rounded-xl border border-border bg-popover shadow-xl z-[90] p-2"
              onMouseEnter={cancelCloseMenus}
              onMouseLeave={scheduleCloseMenus}
            >
              <div
                className="relative"
                onMouseEnter={() => {
                  cancelCloseMenus();
                  setLanguageExpanded(true);
                }}
              >
                <button
                  className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
                  aria-expanded={languageExpanded}
                  aria-controls="sidebar-language-flyout"
                  data-tour="sidebar-language"
                >
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-left truncate">{t('dashboard.language')}</span>
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 ml-auto shrink-0 transition-transform',
                      languageExpanded && 'rotate-90'
                    )}
                  />
                </button>

                {languageExpanded && (
                  <div
                    id="sidebar-language-flyout"
                    className={cn(
                      "absolute min-w-[180px] rounded-xl border border-border bg-popover shadow-xl z-[100]",
                      isDrawer
                        ? "left-0 top-full mt-1"
                        : "left-full top-0 ml-2"
                    )}
                    onMouseEnter={cancelCloseMenus}
                    onMouseLeave={scheduleCloseMenus}
                  >
                    {!isDrawer && <div className="absolute -left-2 top-0 h-full w-2" />}
                    <div className="p-1">
                      {LANGUAGE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setLanguage(option.value)}
                          className={cn(
                            'w-full text-left rounded-lg px-3 py-2 text-sm transition-colors flex items-center gap-2',
                            language === option.value
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-foreground hover:bg-muted'
                          )}
                        >
                          <span className="text-base leading-none" aria-hidden>
                            {option.value === 'pt-BR' ? '🇧🇷' : option.value === 'en' ? '🇺🇸' : '🇪🇸'}
                          </span>
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Restart Tour Button */}
              <button
                onClick={() => {
                  setSettingsExpanded(false);
                  onClose?.();
                  restartTour();
                }}
                className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
              >
                <RotateCcw className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-left truncate">{t('onboarding.controls.restartTour')}</span>
              </button>
            </div>
          )}
        </div>

        <div className="h-px bg-border" />

        {/* Theme Toggle & Collapse */}
        <div data-tour="sidebar-theme" className={cn('flex items-center', effectiveCollapsed ? 'flex-col gap-2' : 'justify-between px-1')}>
          {onThemeToggle && (
            <button
              onClick={onThemeToggle}
              className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              title={editorTheme === 'light' ? t('create.darkMode') : t('create.lightMode')}
            >
              {editorTheme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
          )}
          {/* Hide collapse toggle in drawer mode */}
          {!isDrawer && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              title={effectiveCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            >
              {effectiveCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* User Profile */}
        <div
          className={cn(
            'flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors cursor-pointer',
            effectiveCollapsed && 'justify-center p-2'
          )}
        >
          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0 text-sm font-bold text-primary">
            {displayInitial}
          </div>
          {!effectiveCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-foreground">
                  {displayName}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email || 'email@exemplo.com'}
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                title={t('sidebar.signOut')}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
